import { useState, useEffect, useRef } from 'react'
import * as api from '../../../lib/api'
import { useToast } from '../../../contexts/ToastContext'
import DatePickerField from '../../../components/DatePickerField'
import HlsPlayer from './HlsPlayer'
import CameraAiChat from './CameraAiChat'

// ============================================
// FullscreenPlayer — LIVE + Архив (снапшоты + таймлайн)
// ============================================
function FullscreenPlayer({ camera, onClose }) {
  const [tab, setTab] = useState('live')
  const [archiveDate, setArchiveDate] = useState(() => new Date().toISOString().split('T')[0])
  const [archiveTime, setArchiveTime] = useState('12:00')
  const [archiveEndTime, setArchiveEndTime] = useState('12:05')
  const [muted, setMuted] = useState(true)
  const [liveHlsUrl, setLiveHlsUrl] = useState(null)
  const [snapshotUrl, setSnapshotUrl] = useState(null)
  const [snapshotLoading, setSnapshotLoading] = useState(false)
  const [timeline, setTimeline] = useState([])
  const [timelineLoading, setTimelineLoading] = useState(false)
  const [playing, setPlaying] = useState(false)
  const playIntervalRef = useRef(null)
  const [currentTs, setCurrentTs] = useState(null)
  const [exporting, setExporting] = useState(false)
  const [exportStatus, setExportStatus] = useState(null) // { id, status, progress, video_url, preview_url }
  const exportPollRef = useRef(null)
  const toast = useToast()

  const isIvideon = camera.provider === 'ivideon' && camera.ivideon_server_id
  const fullId = isIvideon ? `${camera.ivideon_server_id}:${camera.ivideon_camera_id || '0'}` : null
  const [showAiChat, setShowAiChat] = useState(false)

  // Загрузить HLS для live стрима
  useEffect(() => {
    if (tab === 'live' && isIvideon) {
      loadLiveUrl()
    }
    return () => {
      if (playIntervalRef.current) clearInterval(playIntervalRef.current)
      if (exportPollRef.current) clearInterval(exportPollRef.current)
      // Очистка авто-фрагмента при размонтировании
      cleanupAutoChunk()
    }
  }, [tab])

  const loadLiveUrl = async () => {
    if (!fullId) return
    try {
      const result = await api.getIvideonLiveUrl(fullId)
      if (result.url) setLiveHlsUrl(result.url)
    } catch (err) {
      console.error('[Fullscreen] Live URL error:', err)
    }
  }

  // Загрузить таймлайн при переключении на архив или смене даты
  useEffect(() => {
    if (tab === 'archive' && isIvideon) {
      loadTimeline()
    }
  }, [tab, archiveDate])

  const loadTimeline = async () => {
    if (!fullId) return
    setTimelineLoading(true)
    try {
      const d = new Date(archiveDate)
      const startOfDay = Math.floor(new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0).getTime() / 1000)
      const endOfDay = startOfDay + 86400
      const result = await api.getIvideonArchiveTimeline(fullId, startOfDay * 1000, endOfDay * 1000)
      setTimeline(result.timeline || [])
    } catch (err) {
      console.error('[Archive] Timeline error:', err)
      setTimeline([])
    } finally {
      setTimelineLoading(false)
    }
  }

  const formatTs = (ts) => {
    if (!ts) return '--:--:--'
    const d = new Date(ts * 1000)
    return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  }

  const [nextExportStatus, setNextExportStatus] = useState(null)
  const nextExportPollRef = useRef(null)
  const [activePlayer, setActivePlayer] = useState(0)
  const videoRef0 = useRef(null)
  const videoRef1 = useRef(null)

  const CHUNK_DURATION = 60 // 1 минута — быстрая загрузка

  useEffect(() => {
    if (exportStatus?.status === 'ready' && exportStatus?.video_url) {
      const activeRef = activePlayer === 0 ? videoRef0 : videoRef1
      if (activeRef.current) {
        activeRef.current.play().catch(() => {})
      }
      // Сразу начинаем грузить следующий
      if (exportStatus._autoChunk && !nextExportStatus) {
        prepareNextChunk(exportStatus._endTs)
      }
    }
  }, [exportStatus?.video_url, exportStatus?.status, exportStatus?._autoChunk, exportStatus?._endTs, activePlayer])

  // ── Запрос видео с определённого времени (основная функция) ──
  const requestVideoFromTime = async (ts, isAutoPlay = false) => {
    if (!fullId) return
    setCurrentTs(ts)
    setExporting(true)
    setExportStatus({ status: 'creating', progress: 0 })
    setNextExportStatus(null) // сброс прелоада
    if (!isAutoPlay) setSnapshotUrl(null)

    if (!isAutoPlay) {
      try {
        const snap = await api.getIvideonArchiveSnapshot(fullId, ts)
        if (snap.url) setSnapshotUrl(snap.url + '&_t=' + Date.now())
      } catch (e) { /* не критично */ }
    }

    const endTs = ts + CHUNK_DURATION
    try {
      const result = await api.exportIvideonMp4(fullId, ts, endTs)
      if (result.success && result.export) {
        const exp = result.export
        exp._autoChunk = true
        exp._startTs = ts
        exp._endTs = endTs
        setExportStatus(exp)
        if (exp.status === 'ready') {
          setExporting(false)
        } else {
          startExportPolling(exp.id, true)
        }
      } else {
        setExportStatus({ status: 'error' })
        toast.error('Не удалось создать видео')
      }
    } catch (err) {
      console.error('[Archive Video] Error:', err)
      setExportStatus({ status: 'error' })
      toast.error('Ошибка: ' + err.message)
    } finally {
      setExporting(false)
    }
  }

  // ── Прелоад следующего фрагмента ──
  const prepareNextChunk = async (ts) => {
    if (!fullId) return
    const endTs = ts + CHUNK_DURATION
    setNextExportStatus({ status: 'creating', _startTs: ts, _endTs: endTs, _autoChunk: true })
    try {
      const result = await api.exportIvideonMp4(fullId, ts, endTs)
      if (result.success && result.export) {
        const exp = result.export
        exp._autoChunk = true
        exp._startTs = ts
        exp._endTs = endTs
        setNextExportStatus(exp)
        if (exp.status !== 'ready') {
          startNextExportPolling(exp.id)
        }
      } else {
        setNextExportStatus(null)
      }
    } catch (err) {
      setNextExportStatus(null)
    }
  }

  const startNextExportPolling = (exportId) => {
    if (nextExportPollRef.current) clearInterval(nextExportPollRef.current)
    nextExportPollRef.current = setInterval(async () => {
      try {
        const result = await api.checkIvideonExport(exportId)
        if (result.success && result.record) {
          const rec = result.record
          setNextExportStatus(prev => {
            if (!prev) return null
            rec._autoChunk = true
            rec._startTs = prev._startTs
            rec._endTs = prev._endTs
            if (rec.status === 'ready' || rec.status === 'error') {
              clearInterval(nextExportPollRef.current)
              nextExportPollRef.current = null
            }
            return rec
          })
        }
      } catch (err) { }
    }, 2000)
  }

  const handleVideoTimeUpdate = (e) => {
    // Больше не используем onTimeUpdate для прелоада, так как теперь грузим СРАЗУ (в useEffect)
    // Но можно оставить для обновления ползунка в будущем
  }

  const handleVideoEnded = () => {
    if (exportStatus?._endTs) {
      cleanupAutoChunk() // удаляем текущий с серверов Ivideon
      if (nextExportStatus && nextExportStatus.status === 'ready' && nextExportStatus.video_url) {
        // Бесшовный переход (пинг-понг плееров)
        setActivePlayer(prev => prev === 0 ? 1 : 0)
        setExportStatus(nextExportStatus)
        setNextExportStatus(null)
      } else {
        // Если следующий фрагмент еще не успел
        requestVideoFromTime(exportStatus._endTs, true)
      }
    }
  }

  const handleGoToTime = () => {
    const [h, m] = archiveTime.split(':').map(Number)
    const d = new Date(archiveDate)
    const ts = Math.floor(new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, m, 0).getTime() / 1000)
    cleanupAutoChunk()
    requestVideoFromTime(ts)
  }

  const cleanupAutoChunk = () => {
    if (exportStatus?._autoChunk && exportStatus?.id) {
      api.deleteIvideonExport(exportStatus.id).catch(() => {})
    }
  }

  // ── Экспорт MP4 (для скачивания с выбранным диапазоном) ──
  const handleExportMp4 = async () => {
    if (!fullId) return
    const [sh, sm] = archiveTime.split(':').map(Number)
    const [eh, em] = archiveEndTime.split(':').map(Number)
    const d = new Date(archiveDate)
    const startTs = Math.floor(new Date(d.getFullYear(), d.getMonth(), d.getDate(), sh, sm, 0).getTime() / 1000)
    const endTs = Math.floor(new Date(d.getFullYear(), d.getMonth(), d.getDate(), eh, em, 0).getTime() / 1000)

    if (endTs <= startTs) {
      toast.warning('Конец должен быть позже начала')
      return
    }
    if (endTs - startTs > 3600) {
      toast.warning('Максимальная длительность — 1 час')
      return
    }

    setExporting(true)
    setExportStatus({ status: 'creating', progress: 0 })
    setNextExportStatus(null)
    setSnapshotUrl(null)
    try {
      const result = await api.exportIvideonMp4(fullId, startTs, endTs)
      if (result.success && result.export) {
        const exp = result.export
        setExportStatus(exp)
        if (exp.status !== 'ready') {
          startExportPolling(exp.id, false)
        }
      } else {
        setExportStatus(null)
        toast.error('Не удалось создать экспорт')
      }
    } catch (err) {
      setExportStatus(null)
      toast.error('Ошибка экспорта: ' + err.message)
    } finally {
      setExporting(false)
    }
  }

  const startExportPolling = (exportId, isAutoChunk) => {
    if (exportPollRef.current) clearInterval(exportPollRef.current)
    exportPollRef.current = setInterval(async () => {
      try {
        const result = await api.checkIvideonExport(exportId)
        if (result.success && result.record) {
          const rec = result.record
          setExportStatus(prev => {
            if (isAutoChunk && prev) {
              rec._autoChunk = true
              rec._startTs = prev._startTs
              rec._endTs = prev._endTs
            }
            return rec
          })
          if (rec.status === 'ready' || rec.status === 'error') {
            clearInterval(exportPollRef.current)
            exportPollRef.current = null
          }
        }
      } catch (err) { }
    }, 2000)
  }

  // Рендер мини-таймлайна
  const handleTimelineClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const clickX = e.clientX - rect.left
    const percentage = Math.max(0, Math.min(1, clickX / rect.width))
    
    const d = new Date(archiveDate)
    const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0).getTime()
    const dayMs = 86400000
    
    const targetMs = dayStart + (percentage * dayMs)
    const targetTs = Math.floor(targetMs / 1000)
    
    cleanupAutoChunk()
    requestVideoFromTime(targetTs)
  }

  const renderTimeline = () => {
    if (!timeline.length) return <div className="archive-timeline-empty">Нет записей за этот день</div>
    const d = new Date(archiveDate)
    const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0).getTime()
    const dayMs = 86400000

    return (
      <div className="archive-timeline-bar">
        <div 
          className="archive-timeline-track" 
          onClick={handleTimelineClick} 
          style={{ cursor: 'pointer' }}
        >
          {/* Фон трека, чтобы клик работал везде */}
          <div style={{ position: 'absolute', inset: 0, zIndex: 1 }} />
          
          {timeline.map((seg, i) => {
            const segStart = seg.start_time - dayStart
            const left = Math.max(0, (segStart / dayMs) * 100)
            const width = Math.max(0.3, (seg.duration / dayMs) * 100)
            const isMotion = seg.type === 3
            return (
              <div
                key={i}
                className={`archive-timeline-segment ${isMotion ? 'motion' : 'continuous'}`}
                style={{ left: `${left}%`, width: `${width}%`, pointerEvents: 'none', zIndex: 2 }}
                title={`${new Date(seg.start_time).toLocaleTimeString('ru-RU')} — ${Math.round(seg.duration / 1000)}с`}
              />
            )
          })}
          {/* Маркер текущей позиции */}
          {currentTs && (() => {
            const pos = ((currentTs * 1000 - dayStart) / dayMs) * 100
            if (pos >= 0 && pos <= 100) {
              return <div className="archive-timeline-cursor" style={{ left: `${pos}%` }} />
            }
            return null
          })()}
        </div>
        <div className="archive-timeline-labels">
          {[0,3,6,9,12,15,18,21].map(h => (
            <span key={h} style={{ left: `${(h/24)*100}%` }}>{String(h).padStart(2,'0')}</span>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="camera-fullscreen-overlay" onClick={onClose}>
      <div className="camera-fullscreen-content fullscreen-with-archive" onClick={e => e.stopPropagation()}>
        <div className="fullscreen-header">
          <h3>{camera.name}</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isIvideon && (
              <div className="fullscreen-tabs">
                <button
                  className={`fs-tab ${tab === 'live' ? 'active' : ''}`}
                  onClick={() => { cleanupAutoChunk(); setTab('live') }}
                >
                  <span className="fs-tab-dot live" />
                  LIVE
                </button>
                <button
                  className={`fs-tab ${tab === 'archive' ? 'active' : ''}`}
                  onClick={() => setTab('archive')}
                >
                  Архив
                </button>
              </div>
            )}
            {(tab === 'live' || tab === 'archive') && (
              <button
                className="camera-sound-btn-fs"
                onClick={() => setMuted(!muted)}
                title={muted ? 'Включить звук' : 'Выключить звук'}
              >
                {muted ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg> : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>}
              </button>
            )}
            {/* Кнопка ИИ-ассистент */}
            {isIvideon && (
              <button
                className={`camera-ai-toggle-btn ${showAiChat ? 'active' : ''}`}
                onClick={() => setShowAiChat(!showAiChat)}
                title={showAiChat ? 'Скрыть ИИ-ассистент' : 'ИИ-нарезка видео'}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a10 10 0 0 1 10 10 10 10 0 0 1-10 10A10 10 0 0 1 2 12 10 10 0 0 1 12 2z"/>
                  <circle cx="12" cy="10" r="3"/>
                  <path d="M7 20.662V19a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1.662"/>
                </svg>
                ИИ
              </button>
            )}
            <button className="fullscreen-close" onClick={onClose}></button>
          </div>
        </div>

        {/* LIVE вкладка */}
        {tab === 'live' && (
          <div className="fullscreen-video-wrapper">
            {isIvideon && liveHlsUrl ? (
              <HlsPlayer
                hlsUrl={liveHlsUrl}
                cameraFullId={fullId}
                muted={muted}
                onMutedChange={setMuted}
                className="fullscreen-hls"
              />
            ) : camera.embed_url ? (
              <iframe
                src={camera.embed_url}
                className="fullscreen-iframe"
                allowFullScreen
                allow="autoplay; fullscreen"
              />
            ) : (
              <div className="fullscreen-no-stream">
                {isIvideon ? 'Загрузка стрима...' : 'Потоковое воспроизведение недоступно'}
              </div>
            )}
          </div>
        )}

        {/* ARCHIVE вкладка */}
        {tab === 'archive' && (
          <div className="archive-content">
            <div className="archive-player-section">
              {/* Видео из экспорта MP4 */}
              {exportStatus?.status === 'ready' && exportStatus.video_url ? (
                <div className="archive-video-wrapper">
                  {/* Плеер 0 */}
                  <video
                    ref={videoRef0}
                    src={activePlayer === 0 ? exportStatus?.video_url : nextExportStatus?.video_url || ''}
                    autoPlay={activePlayer === 0}
                    controls
                    preload="auto"
                    muted={muted}
                    style={{ display: activePlayer === 0 ? 'block' : 'none' }}
                    className="archive-video"
                    onEnded={activePlayer === 0 ? handleVideoEnded : undefined}
                  />
                  {/* Плеер 1 (буфер) */}
                  <video
                    ref={videoRef1}
                    src={activePlayer === 1 ? exportStatus?.video_url : nextExportStatus?.video_url || ''}
                    autoPlay={activePlayer === 1}
                    controls
                    preload="auto"
                    muted={muted}
                    style={{ display: activePlayer === 1 ? 'block' : 'none' }}
                    className="archive-video"
                    onEnded={activePlayer === 1 ? handleVideoEnded : undefined}
                  />
                  <div className="archive-video-badge">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '4px', verticalAlign: 'middle'}}><rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/><line x1="7" y1="2" x2="7" y2="22"/><line x1="17" y1="2" x2="17" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="2" y1="7" x2="7" y2="7"/><line x1="2" y1="17" x2="7" y2="17"/><line x1="17" y1="7" x2="22" y2="7"/><line x1="17" y1="17" x2="22" y2="17"/></svg>{formatTs(exportStatus.start_time)} — {formatTs(exportStatus.end_time)}
                  </div>
                </div>
              ) : snapshotUrl && exportStatus && ['creating', 'in_queue', 'processing'].includes(exportStatus.status) ? (
                <div className="archive-snapshot-wrapper">
                  <img
                    src={snapshotUrl}
                    alt="Превью"
                    className="archive-snapshot-img"
                    style={{ filter: 'brightness(0.6)' }}
                  />
                  <div className="archive-video-loading-overlay">
                    <div className="archive-video-spinner"></div>
                    <div className="archive-video-loading-text">
                      {exportStatus.status === 'creating' && 'Создаём видео...'}
                      {exportStatus.status === 'in_queue' && 'В очереди...'}
                      {exportStatus.status === 'processing' && `Обработка: ${exportStatus.progress || 0}%`}
                    </div>
                  </div>
                  <div className="archive-snapshot-time">{formatTs(currentTs)}</div>
                </div>
              ) : exportStatus && ['creating', 'in_queue', 'processing'].includes(exportStatus.status) ? (
                <div className="archive-placeholder">
                  <div className="archive-video-spinner" style={{ marginBottom: '16px' }}></div>
                  <div className="archive-placeholder-text">
                    {exportStatus.status === 'creating' && 'Создаём видео...'}
                    {exportStatus.status === 'in_queue' && 'Видео в очереди...'}
                    {exportStatus.status === 'processing' && `Обработка: ${exportStatus.progress || 0}%`}
                  </div>
                </div>
              ) : (
                <div className="archive-placeholder">
                  <div className="archive-placeholder-icon"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/><line x1="7" y1="2" x2="7" y2="22"/><line x1="17" y1="2" x2="17" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="2" y1="7" x2="7" y2="7"/><line x1="2" y1="17" x2="7" y2="17"/><line x1="17" y1="7" x2="22" y2="7"/><line x1="17" y1="17" x2="22" y2="17"/></svg></div>
                  <div className="archive-placeholder-text">
                    Выберите время и нажмите «Смотреть»<br/>
                    или кликните по сегменту на таймлайне
                  </div>
                </div>
              )}
            </div>

            {/* Таймлайн */}
            <div className="archive-timeline-section">
              {timelineLoading ? (
                <div className="archive-timeline-loading">Загрузка таймлайна...</div>
              ) : renderTimeline()}
            </div>

            {/* Навигация по дням + быстрые кнопки */}
            <div className="archive-nav-bar">
              <div className="archive-day-nav">
                <button 
                  className="btn-icon-nav" 
                  onClick={() => {
                    const d = new Date(archiveDate)
                    d.setDate(d.getDate() - 1)
                    setArchiveDate(d.toISOString().split('T')[0])
                  }}
                  title="Предыдущий день"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                <DatePickerField
                  value={archiveDate}
                  onChange={(val) => setArchiveDate(val)}
                  placeholder="Дата"
                />
                <button 
                  className="btn-icon-nav" 
                  onClick={() => {
                    const d = new Date(archiveDate)
                    d.setDate(d.getDate() + 1)
                    const today = new Date().toISOString().split('T')[0]
                    const nextDay = d.toISOString().split('T')[0]
                    if (nextDay <= today) setArchiveDate(nextDay)
                  }}
                  title="Следующий день"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              </div>

              {/* Быстрые кнопки навигации ±1/5 мин */}
              <div className="archive-quick-nav">
                <button 
                  className="btn btn-sm btn-ghost"
                  onClick={() => { if (currentTs) { cleanupAutoChunk(); requestVideoFromTime(currentTs - 300) } }}
                  disabled={!currentTs || exporting}
                  title="-5 минут"
                >
                  ◄◄ 5м
                </button>
                <button 
                  className="btn btn-sm btn-ghost"
                  onClick={() => { if (currentTs) { cleanupAutoChunk(); requestVideoFromTime(currentTs - 60) } }}
                  disabled={!currentTs || exporting}
                  title="-1 минута"
                >
                  ◄ 1м
                </button>
                <span className="archive-current-time">
                  {currentTs ? formatTs(currentTs) : '--:--:--'}
                </span>
                <button 
                  className="btn btn-sm btn-ghost"
                  onClick={() => { if (currentTs) { cleanupAutoChunk(); requestVideoFromTime(currentTs + 60) } }}
                  disabled={!currentTs || exporting}
                  title="+1 минута"
                >
                  1м ►
                </button>
                <button 
                  className="btn btn-sm btn-ghost"
                  onClick={() => { if (currentTs) { cleanupAutoChunk(); requestVideoFromTime(currentTs + 300) } }}
                  disabled={!currentTs || exporting}
                  title="+5 минут"
                >
                  5м ►►
                </button>
              </div>
            </div>

            {/* Управление: время + экспорт */}
            <div className="archive-controls">
              <div className="archive-controls-row">
                <div className="archive-control-group">
                  <label>Начало</label>
                  <input
                    type="time"
                    className="archive-input"
                    value={archiveTime}
                    step="1"
                    onChange={e => setArchiveTime(e.target.value)}
                  />
                </div>
                <div className="archive-control-group">
                  <label>Конец</label>
                  <input
                    type="time"
                    className="archive-input"
                    value={archiveEndTime}
                    step="1"
                    onChange={e => setArchiveEndTime(e.target.value)}
                  />
                </div>
                <div className="archive-control-actions">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleGoToTime}
                    disabled={exporting}
                  >
                    {exporting ? <span className="btn-spinner-inline" /> : <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="5 3 19 12 5 21 5 3"/></svg>} Смотреть
                  </button>
                  <button
                    className="btn btn-sm"
                    style={{ background: 'linear-gradient(135deg, #7c3aed, #6366f1)', color: '#fff' }}
                    onClick={handleExportMp4}
                    disabled={exporting}
                    title="Скачать MP4"
                  >
                    {exporting ? <span className="btn-spinner-inline" /> : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>} MP4
                  </button>
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={() => { cleanupAutoChunk(); setTab('live') }}
                    title="Вернуться к трансляции"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3" fill="currentColor"/></svg> Live
                  </button>
                </div>
              </div>
              {/* Export status */}
              {exportStatus && (
                <div className="archive-export-status">
                  {exportStatus.status === 'creating' && <><span className="btn-spinner-inline" /> Создаём экспорт...</>}
                  {exportStatus.status === 'in_queue' && <><span className="btn-spinner-inline" /> В очереди...</>}
                  {exportStatus.status === 'processing' && <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign: 'middle', marginRight: '4px'}}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg> Обработка: {exportStatus.progress || 0}%</>}
                  {exportStatus.status === 'ready' && (
                    <span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign: 'middle', marginRight: '4px'}}><polyline points="20 6 9 17 4 12"/></svg>
                      Готово! ({(exportStatus.file_size / 1024 / 1024).toFixed(1)} МБ)
                      {' '}
                      <a
                        href={exportStatus.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="archive-download-link"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign: 'middle', marginRight: '3px'}}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                        Скачать
                      </a>
                      <button
                        className="archive-cloud-btn"
                        onClick={async () => {
                          try {
                            const date = archiveDate.replace(/-/g, '')
                            const time = formatTs(exportStatus.start_time || currentTs).replace(/:/g, '')
                            const filename = `${camera.name}_${date}_${time}.mp4`
                            const resp = await fetch('/api/cloud-storage/yandex/upload', {
                              method: 'POST',
                              headers: { 
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                              },
                              body: JSON.stringify({
                                video_url: exportStatus.video_url,
                                filename,
                                folder_path: `/PVZ-Камеры/${camera.name}`
                              })
                            })
                            const data = await resp.json()
                            if (data.success) {
                              toast.success(data.message || 'Сохранено на Яндекс.Диск')
                            } else {
                              toast.error(data.error || 'Ошибка сохранения')
                            }
                          } catch (err) {
                            toast.error('Ошибка: ' + err.message)
                          }
                        }}
                        title="Сохранить на Яндекс.Диск"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign: 'middle', marginRight: '3px'}}><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                        Я.Диск
                      </button>
                    </span>
                  )}
                  {exportStatus.status === 'error' && <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign: 'middle', marginRight: '4px'}}><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg> Ошибка экспорта</>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ИИ-чат панель */}
        {showAiChat && (
          <CameraAiChat
            camera={camera}
            onSeekArchive={(date, time) => {
              setTab('archive')
              setArchiveDate(date)
              setArchiveTime(time)
              // Auto-navigate to the time
              const [h, m, s] = time.split(':').map(Number)
              const d = new Date(date)
              const ts = Math.floor(new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, m, s || 0).getTime() / 1000)
              cleanupAutoChunk()
              requestVideoFromTime(ts)
            }}
            onClose={() => setShowAiChat(false)}
          />
        )}
      </div>
    </div>
  )
}

export default FullscreenPlayer
